import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { Toast as ToastType } from '../contexts/ToastContext';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $type: ToastType['type'], $isLeaving: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 500px;
  position: relative;
  animation: ${props => props.$isLeaving ? slideOut : slideIn} 0.5s ease-out;
  cursor: pointer;

  background-color: ${props => {
    switch (props.$type) {
      case 'success': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'warning': return '#fff3cd';
      case 'info': return '#d1ecf1';
      default: return '#f8f9fa';
    }
  }};

  border-left: 4px solid ${props => {
    switch (props.$type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  }};

  color: ${props => {
    switch (props.$type) {
      case 'success': return '#155724';
      case 'error': return '#721c24';
      case 'warning': return '#856404';
      case 'info': return '#0c5460';
      default: return '#495057';
    }
  }};
`;

const IconWrapper = styled.div<{ $type: ToastType['type'] }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  color: ${props => {
    switch (props.$type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  }};
`;

const Message = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  color: inherit;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const ProgressBar = styled.div<{ $duration: number, $type: ToastType['type'] }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 0 0 8px 8px;
  animation: progress ${props => props.$duration}ms linear;

  background-color: ${props => {
    switch (props.$type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  }};

  @keyframes progress {
    from { width: 100%; }
    to { width: 0%; }
  }
`;

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const getIcon = (type: ToastType['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircle size={20} />;
    case 'error':
      return <XCircle size={20} />;
    case 'warning':
      return <AlertTriangle size={20} />;
    case 'info':
      return <Info size={20} />;
    default:
      return <Info size={20} />;
  }
};

export const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 500);
  };

  useEffect(() => {
    const duration = toast.duration || 6000;
    const timer = setTimeout(() => {
      handleRemove();
    }, duration);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.duration, toast.id]);

  return (
    <ToastContainer $type={toast.type} $isLeaving={isLeaving}>
      <IconWrapper $type={toast.type}>
        {getIcon(toast.type)}
      </IconWrapper>

      <Message>{toast.message}</Message>

      <CloseButton onClick={handleRemove}>
        <X size={16} />
      </CloseButton>

      <ProgressBar $duration={toast.duration || 6000} $type={toast.type} />
    </ToastContainer>
  );
};